import React from 'react';

// Skeleton animation styles
const skeletonStyles = `
  @keyframes skeleton-shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
  
  .skeleton {
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.05) 25%,
      rgba(255, 255, 255, 0.1) 50%,
      rgba(255, 255, 255, 0.05) 75%
    );
    background-size: 200% 100%;
    animation: skeleton-shimmer 1.5s ease-in-out infinite;
    border-radius: 8px;
  }
`;

// Base skeleton component
const Skeleton = ({ width, height, borderRadius, style = {} }) => (
  <div
    className="skeleton"
    style={{
      width: width || '100%',
      height: height || '20px',
      borderRadius: borderRadius || '8px',
      ...style
    }}
  />
);

// Skeleton for text lines
export const SkeletonText = ({ lines = 1, width = '100%', gap = '8px' }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap }}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        width={i === lines - 1 && lines > 1 ? '70%' : width}
        height="14px"
      />
    ))}
  </div>
);

// Skeleton for cards
export const SkeletonCard = ({ height = '120px' }) => (
  <div style={{
    padding: '20px',
    background: 'rgba(20, 20, 25, 0.85)',
    border: '1px solid rgba(200, 180, 50, 0.15)',
    borderRadius: '16px'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
      <Skeleton width="40px" height="40px" borderRadius="10px" />
      <div style={{ flex: 1 }}>
        <Skeleton width="60%" height="16px" style={{ marginBottom: '8px' }} />
        <Skeleton width="40%" height="12px" />
      </div>
    </div>
    <Skeleton width="100%" height="60px" />
  </div>
);

// Skeleton for launch history items
export const SkeletonHistoryItem = () => (
  <div style={{
    padding: '16px',
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  }}>
    <Skeleton width="44px" height="44px" borderRadius="10px" />
    <div style={{ flex: 1 }}>
      <Skeleton width="120px" height="16px" style={{ marginBottom: '6px' }} />
      <Skeleton width="200px" height="12px" />
    </div>
    <Skeleton width="80px" height="32px" borderRadius="8px" />
  </div>
);

// Skeleton for launch history panel
export const SkeletonLaunchHistory = ({ itemCount = 3 }) => (
  <div style={{
    padding: '24px',
    background: 'rgba(20, 20, 25, 0.85)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(200, 180, 50, 0.15)',
    borderRadius: '20px',
    marginBottom: '30px'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Skeleton width="40px" height="40px" borderRadius="10px" />
        <div>
          <Skeleton width="140px" height="18px" style={{ marginBottom: '6px' }} />
          <Skeleton width="100px" height="12px" />
        </div>
      </div>
      <Skeleton width="100px" height="36px" borderRadius="8px" />
    </div>
    
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {Array.from({ length: itemCount }).map((_, i) => (
        <SkeletonHistoryItem key={i} />
      ))}
    </div>
  </div>
);

// Skeleton for token balance
export const SkeletonBalance = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '12px'
  }}>
    <Skeleton width="20px" height="20px" borderRadius="50%" />
    <Skeleton width="80px" height="18px" />
  </div>
);

// Skeleton for feature cards grid
export const SkeletonFeatureGrid = ({ count = 4 }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '40px'
  }}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} style={{
        padding: '24px',
        background: 'rgba(20, 20, 25, 0.85)',
        border: '1px solid rgba(200, 180, 50, 0.15)',
        borderRadius: '20px',
        textAlign: 'center'
      }}>
        <Skeleton width="50px" height="50px" borderRadius="14px" style={{ margin: '0 auto 16px' }} />
        <Skeleton width="80%" height="16px" style={{ margin: '0 auto 8px' }} />
        <Skeleton width="60%" height="12px" style={{ margin: '0 auto' }} />
      </div>
    ))}
  </div>
);

// Skeleton for holder snapshot table
export const SkeletonHolderTable = ({ rows = 5 }) => (
  <div style={{
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '12px',
    overflow: 'hidden'
  }}>
    {/* Header */}
    <div style={{
      display: 'grid',
      gridTemplateColumns: '60px 1fr 120px 100px',
      gap: '12px',
      padding: '12px 16px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
    }}>
      <Skeleton width="40px" height="14px" />
      <Skeleton width="80px" height="14px" />
      <Skeleton width="60px" height="14px" />
      <Skeleton width="50px" height="14px" />
    </div>
    
    {/* Rows */}
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} style={{
        display: 'grid',
        gridTemplateColumns: '60px 1fr 120px 100px',
        gap: '12px',
        padding: '14px 16px',
        borderBottom: i < rows - 1 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none'
      }}>
        <Skeleton width="24px" height="16px" />
        <Skeleton width="180px" height="16px" />
        <Skeleton width="80px" height="16px" />
        <Skeleton width="50px" height="16px" />
      </div>
    ))}
  </div>
);

// Main SkeletonLoader component with style injection
const SkeletonLoader = ({ children }) => (
  <>
    <style>{skeletonStyles}</style>
    {children}
  </>
);

// Export all skeleton components
SkeletonLoader.Text = SkeletonText;
SkeletonLoader.Card = SkeletonCard;
SkeletonLoader.HistoryItem = SkeletonHistoryItem;
SkeletonLoader.LaunchHistory = SkeletonLaunchHistory;
SkeletonLoader.Balance = SkeletonBalance;
SkeletonLoader.FeatureGrid = SkeletonFeatureGrid;
SkeletonLoader.HolderTable = SkeletonHolderTable;
SkeletonLoader.Base = Skeleton;

export default SkeletonLoader;
